/**
 * ====================================================================
 * FIKO FEATURE CONTROL FRAME (FCF) - 3D COMPONENT
 * ====================================================================
 *
 * ASME Y14.5-2018 ve ISO 1101:2017 uyumlu profesyonel
 * Feature Control Frame 3D rendering komponenti.
 *
 * Özellikler:
 * - Tam ASME Y14.5-2018 sembol desteği
 * - Composite tolerance desteği
 * - Leader line ile geometriye bağlanma
 * - Zoom'a göre sabit boyut
 * - Yüksek performanslı Canvas rendering
 * - Material pooling
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';
import {
    GDTCharacteristicType,
    MaterialCondition,
    ToleranceZoneModifier,
    DatumReference,
    FCFComponent,
    FeatureControlFrameConfig,
    FCFStyleConfig,
    GDT_SYMBOLS,
    MATERIAL_CONDITION_SYMBOLS,
    TOLERANCE_ZONE_SYMBOLS,
    formatToleranceValue,
    requiresDatum,
    isDeprecated
} from './GDTTypes';
import { drawGDTSymbol, drawMaterialCondition, drawToleranceZoneModifier } from './GDTSymbols';

// ====================================================================
// VARSAYILAN DEĞERLER
// ====================================================================

const DEFAULT_STYLE: Required<FCFStyleConfig> = {
    frameColor: '#FFFFFF',
    borderColor: '#FFFFFF',  // Alias for frameColor
    textColor: '#FFFFFF',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    frameWidth: 2,
    lineWidth: 2,  // Alias for frameWidth
    fontSize: 14,
    fontFamily: 'Arial, sans-serif',
    fixedSize: true,
    leaderColor: '#FFFFFF',
    leaderWidth: 1.5,
    leaderType: 'solid'
};

const CELL_HEIGHT = 28;
const CELL_PADDING = 6;
const SYMBOL_CELL_WIDTH = 32;
const MIN_VALUE_CELL_WIDTH = 50;
const DATUM_CELL_WIDTH = 28;

// ====================================================================
// MATERIAL POOLING
// ====================================================================

const materialPool = new Map<string, THREE.SpriteMaterial>();
const geometryPool = {
    line: new THREE.BufferGeometry(),
    cone: new THREE.ConeGeometry(0.15, 0.4, 8)
};

function getOrCreateMaterial(key: string, creator: () => THREE.SpriteMaterial): THREE.SpriteMaterial {
    if (!materialPool.has(key)) {
        materialPool.set(key, creator());
    }
    return materialPool.get(key)!;
}

// ====================================================================
// FEATURE CONTROL FRAME CLASS
// ====================================================================

/**
 * Professional Feature Control Frame 3D Component
 */
export class FeatureControlFrame extends THREE.Group {
    override type = 'FeatureControlFrame';
    override name = 'Feature Control Frame';

    // ═══════════════════════════════════════════════════════════════
    // PROPERTIES
    // ═══════════════════════════════════════════════════════════════

    /** Sabit boyut (zoom'a göre scale) */
    sabitBoyut: boolean = true;

    /** Konfigürasyon */
    private config: FeatureControlFrameConfig;

    /** Stil ayarları */
    private style: Required<FCFStyleConfig>;

    /** Sahne referansı */
    public sahne: FikoSahne;

    /** Canvas ve texture */
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    /** Sprite (FCF görüntüsü) */
    private sprite: THREE.Sprite | null = null;

    /** Leader line */
    private leaderLine: THREE.Line | null = null;
    private leaderArrow: THREE.Mesh | null = null;

    /** Event listeners */
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    /** Hesaplanan boyutlar */
    private calculatedWidth: number = 0;
    private calculatedHeight: number = 0;

    // ═══════════════════════════════════════════════════════════════
    // CONSTRUCTOR
    // ═══════════════════════════════════════════════════════════════

    constructor(
        sahne: FikoSahne,
        config: FeatureControlFrameConfig
    ) {
        super();

        this.sahne = sahne;
        this.config = config;
        this.style = { ...DEFAULT_STYLE, ...config.style };
        this.sabitBoyut = this.style.fixedSize;

        // Canvas setup
        this.canvas = document.createElement('canvas');
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        // Render settings
        this.renderOrder = 999;
        this.frustumCulled = false;

        // ID ve isim
        if (config.id) this.uuid = config.id;
        if (config.name) this.name = config.name;

        // Initialize
        this.init();
        this.hazirla(sahne);
    }

    // ═══════════════════════════════════════════════════════════════
    // INITIALIZATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Initialize FCF
     */
    private init(): void {
        // Calculate dimensions
        this.calculateDimensions();

        // Render to canvas
        this.renderToCanvas();

        // Create sprite
        this.createSprite();

        // Create leader line if target point exists
        if (this.config.targetPoint) {
            this.createLeaderLine();
        }
    }

    /**
     * Calculate total dimensions based on components
     */
    private calculateDimensions(): void {
        const ctx = this.context;
        ctx.font = `bold ${this.style.fontSize}px ${this.style.fontFamily}`;

        let maxRowWidth = 0;

        for (const component of this.config.components) {
            let rowWidth = CELL_PADDING; // Left padding

            // Symbol cell
            rowWidth += SYMBOL_CELL_WIDTH;

            // Zone modifier (if any)
            if (component.zoneModifier && component.zoneModifier !== ToleranceZoneModifier.NONE) {
                const symbol = TOLERANCE_ZONE_SYMBOLS[component.zoneModifier];
                rowWidth += ctx.measureText(symbol).width + CELL_PADDING * 2;
            }

            // Tolerance value cell
            const valueText = formatToleranceValue(component.toleranceValue, component.unit);
            rowWidth += Math.max(MIN_VALUE_CELL_WIDTH, ctx.measureText(valueText).width + CELL_PADDING * 2);

            // Material condition (if any)
            if (component.toleranceMaterialCondition &&
                component.toleranceMaterialCondition !== MaterialCondition.RFS) {
                rowWidth += DATUM_CELL_WIDTH;
            }

            // Datum cells
            if (component.primaryDatum) rowWidth += DATUM_CELL_WIDTH;
            if (component.secondaryDatum) rowWidth += DATUM_CELL_WIDTH;
            if (component.tertiaryDatum) rowWidth += DATUM_CELL_WIDTH;

            rowWidth += CELL_PADDING; // Right padding
            maxRowWidth = Math.max(maxRowWidth, rowWidth);
        }

        this.calculatedWidth = maxRowWidth;
        this.calculatedHeight = CELL_HEIGHT * this.config.components.length;
    }

    // ═══════════════════════════════════════════════════════════════
    // CANVAS RENDERING
    // ═══════════════════════════════════════════════════════════════

    /**
     * Render FCF to canvas
     */
    private renderToCanvas(): void {
        const scale = 2; // Retina/HiDPI
        const width = this.calculatedWidth;
        const height = this.calculatedHeight;

        // Set canvas size
        this.canvas.width = width * scale;
        this.canvas.height = height * scale;

        const ctx = this.context;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, width, height);

        // Background
        ctx.fillStyle = this.style.backgroundColor;
        ctx.fillRect(0, 0, width, height);

        // Draw each component row
        let y = 0;
        for (const component of this.config.components) {
            this.renderComponentRow(ctx, component, 0, y, width);
            y += CELL_HEIGHT;
        }

        // Outer frame
        ctx.strokeStyle = this.style.frameColor;
        ctx.lineWidth = this.style.frameWidth;
        ctx.strokeRect(1, 1, width - 2, height - 2);

        // Update texture
        this.texture.needsUpdate = true;
    }

    /**
     * Render single FCF component row
     */
    private renderComponentRow(
        ctx: CanvasRenderingContext2D,
        component: FCFComponent,
        startX: number,
        startY: number,
        totalWidth: number
    ): void {
        const cellH = CELL_HEIGHT;
        let x = startX;

        ctx.strokeStyle = this.style.frameColor;
        ctx.fillStyle = this.style.textColor;
        ctx.lineWidth = 1;
        ctx.font = `bold ${this.style.fontSize}px ${this.style.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // ─────────────────────────────────────────────────────────────
        // 1. SYMBOL CELL
        // ─────────────────────────────────────────────────────────────
        const symbolWidth = SYMBOL_CELL_WIDTH;

        // Cell border
        ctx.strokeRect(x, startY, symbolWidth, cellH);

        // Draw GD&T symbol
        const symbolSize = cellH * 0.7;
        drawGDTSymbol(
            ctx,
            component.characteristic,
            x + (symbolWidth - symbolSize) / 2,
            startY + (cellH - symbolSize) / 2,
            {
                size: symbolSize,
                color: this.style.textColor,
                lineWidth: 1.5
            }
        );

        // Deprecated warning (subtle indicator)
        if (isDeprecated(component.characteristic)) {
            ctx.fillStyle = '#FF6B6B';
            ctx.font = `bold 8px ${this.style.fontFamily}`;
            ctx.fillText('!', x + symbolWidth - 6, startY + 8);
            ctx.fillStyle = this.style.textColor;
            ctx.font = `bold ${this.style.fontSize}px ${this.style.fontFamily}`;
        }

        x += symbolWidth;

        // ─────────────────────────────────────────────────────────────
        // 2. ZONE MODIFIER (optional)
        // ─────────────────────────────────────────────────────────────
        if (component.zoneModifier && component.zoneModifier !== ToleranceZoneModifier.NONE) {
            const symbol = TOLERANCE_ZONE_SYMBOLS[component.zoneModifier];
            const modWidth = ctx.measureText(symbol).width + CELL_PADDING * 2;

            ctx.strokeRect(x, startY, modWidth, cellH);
            ctx.fillText(symbol, x + modWidth / 2, startY + cellH / 2);

            x += modWidth;
        }

        // ─────────────────────────────────────────────────────────────
        // 3. TOLERANCE VALUE CELL
        // ─────────────────────────────────────────────────────────────
        const valueText = formatToleranceValue(component.toleranceValue, component.unit);
        const valueWidth = Math.max(MIN_VALUE_CELL_WIDTH, ctx.measureText(valueText).width + CELL_PADDING * 2);

        ctx.strokeRect(x, startY, valueWidth, cellH);
        ctx.fillText(valueText, x + valueWidth / 2, startY + cellH / 2);

        x += valueWidth;

        // ─────────────────────────────────────────────────────────────
        // 4. MATERIAL CONDITION (optional)
        // ─────────────────────────────────────────────────────────────
        if (component.toleranceMaterialCondition &&
            component.toleranceMaterialCondition !== MaterialCondition.RFS) {

            ctx.strokeRect(x, startY, DATUM_CELL_WIDTH, cellH);

            const mcSymbol = MATERIAL_CONDITION_SYMBOLS[component.toleranceMaterialCondition];
            if (mcSymbol) {
                // Draw circled letter
                const mcSize = cellH * 0.6;
                const mcX = x + (DATUM_CELL_WIDTH - mcSize) / 2;
                const mcY = startY + (cellH - mcSize) / 2;

                // Circle
                ctx.beginPath();
                ctx.arc(
                    mcX + mcSize / 2,
                    mcY + mcSize / 2,
                    mcSize / 2 - 1,
                    0,
                    Math.PI * 2
                );
                ctx.stroke();

                // Letter
                const letter = component.toleranceMaterialCondition === MaterialCondition.MMC ||
                               component.toleranceMaterialCondition === MaterialCondition.MMB ? 'M' : 'L';
                ctx.font = `bold ${mcSize * 0.6}px ${this.style.fontFamily}`;
                ctx.fillText(letter, x + DATUM_CELL_WIDTH / 2, startY + cellH / 2);
                ctx.font = `bold ${this.style.fontSize}px ${this.style.fontFamily}`;
            }

            x += DATUM_CELL_WIDTH;
        }

        // ─────────────────────────────────────────────────────────────
        // 5. DATUM REFERENCES
        // ─────────────────────────────────────────────────────────────
        const datums = [
            component.primaryDatum,
            component.secondaryDatum,
            component.tertiaryDatum
        ].filter(Boolean) as DatumReference[];

        for (const datum of datums) {
            ctx.strokeRect(x, startY, DATUM_CELL_WIDTH, cellH);

            let datumText = datum.letter;
            if (datum.secondaryLetter) {
                datumText += `-${datum.secondaryLetter}`;
            }

            ctx.fillText(datumText, x + DATUM_CELL_WIDTH / 2, startY + cellH / 2);

            // Material condition for datum (if any)
            if (datum.materialCondition && datum.materialCondition !== MaterialCondition.RFS) {
                const mcSymbol = MATERIAL_CONDITION_SYMBOLS[datum.materialCondition];
                if (mcSymbol) {
                    ctx.font = `bold ${this.style.fontSize * 0.7}px ${this.style.fontFamily}`;
                    ctx.fillText(mcSymbol, x + DATUM_CELL_WIDTH - 4, startY + cellH - 6);
                    ctx.font = `bold ${this.style.fontSize}px ${this.style.fontFamily}`;
                }
            }

            x += DATUM_CELL_WIDTH;
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SPRITE CREATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Create 3D sprite from canvas
     */
    private createSprite(): void {
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;
        this.texture.generateMipmaps = false;

        const material = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false,
            depthWrite: false
        });

        this.sprite = new THREE.Sprite(material);
        this.sprite.position.copy(this.config.position);

        // Scale based on canvas aspect ratio
        const aspect = this.calculatedWidth / this.calculatedHeight;
        const baseScale = 2;
        this.sprite.scale.set(baseScale * aspect, baseScale, 1);

        this.sprite.renderOrder = 999;
        this.add(this.sprite);
    }

    // ═══════════════════════════════════════════════════════════════
    // LEADER LINE
    // ═══════════════════════════════════════════════════════════════

    /**
     * Create leader line to target point
     */
    private createLeaderLine(): void {
        if (!this.config.targetPoint || !this.sprite) return;

        // Leader line geometry
        const positions = new Float32Array([
            this.config.position.x, this.config.position.y, this.config.position.z,
            this.config.targetPoint.x, this.config.targetPoint.y, this.config.targetPoint.z
        ]);

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Line material
        const material = this.style.leaderType === 'dashed'
            ? new THREE.LineDashedMaterial({
                color: this.style.leaderColor,
                linewidth: this.style.leaderWidth,
                dashSize: 0.2,
                gapSize: 0.1,
                depthTest: false
            })
            : new THREE.LineBasicMaterial({
                color: this.style.leaderColor,
                linewidth: this.style.leaderWidth,
                depthTest: false
            });

        this.leaderLine = new THREE.Line(geometry, material);
        this.leaderLine.computeLineDistances();
        this.leaderLine.renderOrder = 998;
        this.add(this.leaderLine);

        // Arrow head at target
        this.createLeaderArrow();
    }

    /**
     * Create arrow head for leader line
     */
    private createLeaderArrow(): void {
        if (!this.config.targetPoint) return;

        const arrowMaterial = new THREE.MeshBasicMaterial({
            color: this.style.leaderColor,
            depthTest: false
        });

        this.leaderArrow = new THREE.Mesh(geometryPool.cone, arrowMaterial);
        this.leaderArrow.position.copy(this.config.targetPoint);

        // Orient arrow towards FCF
        const direction = new THREE.Vector3()
            .subVectors(this.config.position, this.config.targetPoint)
            .normalize();

        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        this.leaderArrow.quaternion.copy(quaternion);

        this.leaderArrow.renderOrder = 998;
        this.add(this.leaderArrow);
    }

    // ═══════════════════════════════════════════════════════════════
    // FIXED SIZE HANDLING
    // ═══════════════════════════════════════════════════════════════

    /**
     * Initialize fixed size behavior
     */
    hazirla(sahne?: FikoSahne): void {
        if (!sahne?.anaGosterim?.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            console.warn('FCF hazirla error:', e);
        }
    }

    /**
     * Handle camera zoom changes
     */
    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;

        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;

        this.sprite.scale.set(
            originalScale.x * scaleAdjustment,
            originalScale.y * scaleAdjustment,
            1
        );
        this.sprite.updateMatrix();

        // Scale leader arrow too
        if (this.leaderArrow) {
            const arrowScale = scaleAdjustment * 0.5;
            this.leaderArrow.scale.set(arrowScale, arrowScale, arrowScale);
        }
    }

    /**
     * Set fixed size mode
     */
    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.sprite) {
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
            this.kameraYonDegisti();
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // UPDATE METHODS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Update FCF configuration
     */
    updateConfig(newConfig: Partial<FeatureControlFrameConfig>): void {
        this.config = { ...this.config, ...newConfig };
        if (newConfig.style) {
            this.style = { ...this.style, ...newConfig.style };
        }

        // Re-render
        this.calculateDimensions();
        this.renderToCanvas();

        // Update sprite scale
        if (this.sprite) {
            const aspect = this.calculatedWidth / this.calculatedHeight;
            const baseScale = 2;
            this.sprite.scale.set(baseScale * aspect, baseScale, 1);
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
        }

        // Update position
        if (newConfig.position && this.sprite) {
            this.sprite.position.copy(newConfig.position);
        }

        // Update leader line
        if (newConfig.targetPoint && this.leaderLine) {
            this.updateLeaderLine();
        }
    }

    /**
     * Update leader line positions
     */
    private updateLeaderLine(): void {
        if (!this.leaderLine || !this.config.targetPoint) return;

        const positions = this.leaderLine.geometry.getAttribute('position') as THREE.BufferAttribute;
        positions.setXYZ(0, this.config.position.x, this.config.position.y, this.config.position.z);
        positions.setXYZ(1, this.config.targetPoint.x, this.config.targetPoint.y, this.config.targetPoint.z);
        positions.needsUpdate = true;

        // Update arrow
        if (this.leaderArrow) {
            this.leaderArrow.position.copy(this.config.targetPoint);

            const direction = new THREE.Vector3()
                .subVectors(this.config.position, this.config.targetPoint)
                .normalize();

            const quaternion = new THREE.Quaternion();
            quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
            this.leaderArrow.quaternion.copy(quaternion);
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SERIALIZATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Serialize to JSON (avoid conflict with THREE.Object3D.toJSON)
     */
    serialize(): FeatureControlFrameConfig {
        return {
            id: this.uuid,
            name: this.name,
            position: this.config.position.clone(),
            components: this.config.components.map(c => ({ ...c })),
            targetPoint: this.config.targetPoint?.clone(),
            style: { ...this.style }
        };
    }

    /**
     * Create from JSON
     */
    static fromJSON(sahne: FikoSahne, json: FeatureControlFrameConfig): FeatureControlFrame {
        return new FeatureControlFrame(sahne, {
            ...json,
            position: new THREE.Vector3(
                json.position.x,
                json.position.y,
                json.position.z
            ),
            targetPoint: json.targetPoint ? new THREE.Vector3(
                json.targetPoint.x,
                json.targetPoint.y,
                json.targetPoint.z
            ) : undefined
        });
    }

    // ═══════════════════════════════════════════════════════════════
    // VALIDATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Validate FCF configuration
     */
    validate(): { valid: boolean; errors: string[]; warnings: string[] } {
        const errors: string[] = [];
        const warnings: string[] = [];

        for (const component of this.config.components) {
            // Check if datum is required but missing
            if (requiresDatum(component.characteristic)) {
                if (!component.primaryDatum) {
                    errors.push(
                        `${component.characteristic} requires at least one datum reference`
                    );
                }
            }

            // Check for deprecated characteristics
            if (isDeprecated(component.characteristic)) {
                warnings.push(
                    `${component.characteristic} is deprecated in ASME Y14.5-2018. Consider using POSITION instead.`
                );
            }

            // Check tolerance value
            if (component.toleranceValue <= 0) {
                errors.push('Tolerance value must be positive');
            }
        }

        return {
            valid: errors.length === 0,
            errors,
            warnings
        };
    }

    // ═══════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════

    /**
     * Dispose resources
     */
    dispose(): void {
        // Remove event listener
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        // Dispose sprite
        if (this.sprite) {
            (this.sprite.material as THREE.SpriteMaterial).map?.dispose();
            (this.sprite.material as THREE.SpriteMaterial).dispose();
            this.remove(this.sprite);
            this.sprite = null;
        }

        // Dispose leader line
        if (this.leaderLine) {
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
            this.remove(this.leaderLine);
            this.leaderLine = null;
        }

        // Dispose leader arrow
        if (this.leaderArrow) {
            (this.leaderArrow.material as THREE.Material).dispose();
            this.remove(this.leaderArrow);
            this.leaderArrow = null;
        }

        // Dispose texture
        this.texture.dispose();
    }
}

// ====================================================================
// FACTORY FUNCTION
// ====================================================================

/**
 * Quick FCF creation helper
 */
export function createFCF(
    sahne: FikoSahne,
    position: THREE.Vector3,
    characteristic: GDTCharacteristicType,
    toleranceValue: number,
    options?: {
        zoneModifier?: ToleranceZoneModifier;
        materialCondition?: MaterialCondition;
        datums?: [string?, string?, string?];
        targetPoint?: THREE.Vector3;
        style?: Partial<FCFStyleConfig>;
    }
): FeatureControlFrame {
    const component: FCFComponent = {
        characteristic,
        toleranceValue,
        zoneModifier: options?.zoneModifier,
        toleranceMaterialCondition: options?.materialCondition
    };

    // Add datums
    if (options?.datums) {
        if (options.datums[0]) {
            component.primaryDatum = { letter: options.datums[0], type: 'SINGLE' as any };
        }
        if (options.datums[1]) {
            component.secondaryDatum = { letter: options.datums[1], type: 'SINGLE' as any };
        }
        if (options.datums[2]) {
            component.tertiaryDatum = { letter: options.datums[2], type: 'SINGLE' as any };
        }
    }

    return new FeatureControlFrame(sahne, {
        position,
        components: [component],
        targetPoint: options?.targetPoint,
        style: options?.style
    });
}
